import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { CompetitionImportService } from "../../../shared/services/competition-import.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AuthService } from "../../../shared/services/auth.service";
import { FilterService } from "../../../shared/services/filter.service";
import { select, Store } from "@ngrx/store";
import { StoreRootState } from "../../../shared/ngrx/reducers";
import { delay, distinctUntilChanged, takeUntil, tap } from "rxjs/operators";
import { CountedAndPaginated } from "../../../shared/interface/response/counted-and-paginated";
import { CompetitionImportRow } from "../../../shared/interface/competition-import-row";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { getCurrentRouteState } from "../../../shared/ngrx/selector";
import { RouterStateUrl } from "../../../shared/ngrx/route-serializer";
import { NgForm, FormBuilder, FormControl } from "@angular/forms";
import { ReplaySubject } from "rxjs";
import { UserPermissions } from "../../../shared/interface/user-permissions";
import { CompetitionImportColumn } from "../../../shared/interface/competition-import-column";
import { MatSort } from '@angular/material/sort';
import {CompetitionWzService} from "../../../shared/services/competition-wz.service";
import {
  WZ_STATUS_IN_PREPARATION,
  WZ_STATUS_TO_RELEASE
} from "../../../shared/services/dict.service";
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {MatDialog} from "@angular/material/dialog";

export interface CompetitionImportRowWithCheckbox extends CompetitionImportRow {
  checkbox: FormControl;
}

@Component({
  selector: 'app-competition-import-values-list',
  templateUrl: './competition-import-values-list.component.html',
  styleUrls: ['./competition-import-values-list.component.scss']
})
export class CompetitionImportValuesListComponent implements AfterViewInit, OnDestroy {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatSort) sort: MatSort;

  readonly WZ_STATUS_TO_RELEASE = WZ_STATUS_TO_RELEASE;
  readonly WZ_STATUS_IN_PREPARATION = WZ_STATUS_IN_PREPARATION;

  participantsDataSource: CompetitionImportRowWithCheckbox[] = [];

  initialFilters: any;
  displayedColumns: string[] = [
    'checkbox',
    'awardId',
    'status',
    'amount',
    'protocol',
    'laureate',
    'competitionSettledOn',
    'assetsRegional',
    'competition',
  ];

  sortBy = null;
  sortOrder = null;
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  values: CompetitionImportColumn[] = [];

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private competitionImportService: CompetitionImportService,
    private competitionWzService: CompetitionWzService,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
    private fb: FormBuilder,
    private dialog: MatDialog,
  ) {
    this.userPermissions = auth.getUserPermissions();

    this.competitionImportService.importRowsListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<CompetitionImportRow>) => {
          this.participantsDataSource = e?.items?.map(item => ({
            ...item,
            checkbox: this.fb.control(false)
          })) || [];
          this.totalData = e?.pagination?.total;
        })
      )
      .subscribe();
    this.parseFilters();
  }

  get hasSelectedItems(): boolean {
    return this.participantsDataSource.some(item => item.checkbox && item.checkbox.value);
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      partner: 'string',
      ofwca: 'string',
      regional: 'string[]',
      competitionName: 'string',
      warehouseProduct: 'string',
      settledOn: 'int',
      protocol: 'int',
      status: 'int',
      addedProtocol: 'int',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((event: PageEvent) => {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
          ...this.filtersForm?.value
        })]);
      });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.loadListData();
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.competitionImportService.loadImportRowsList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.initialFilters
    );
  }

  filter(): void {
    const formValues = { ...this.filtersForm.value };

    for (const key in formValues) {
      if (formValues[key] === undefined || formValues[key] === null || formValues[key] === '') {
        delete formValues[key];
      }
    }

    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path,
      this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator.pageSize,
        ...formValues
      })
    ]);
  }

  addToWZ(): void {
    const selectedItems = this.getSelectedItems();
    const competitionRowIds = selectedItems.map(item => item.uuid);

    this.competitionWzService.addToWZ(competitionRowIds).subscribe({
      next: (response: Uuid) => {
        this.snack.open('Pomyślnie utworzono WZ', 'OK', { duration: 3000 });
        this.participantsDataSource.forEach(item => item.checkbox.setValue(false));
        this.loadListData();

        if (response && response.uuid) {
          this.router.navigate(['/wyniki-konkursow/wydania-z-magazynu/szczegoly', response.uuid]);
        }
      },
      error: () => {
        this.snack.open('Wystąpił błąd podczas dodawania do WZ', 'OK', { duration: 3000 });
      }
    });
  }

  resetFilters(): void {
    this.filtersForm?.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator?.pageSize || 10,
    }]);
  }

  getSelectedItems(): CompetitionImportRow[] {
    return this.participantsDataSource
      .filter(item => item.checkbox.value)
      .map(({ checkbox, ...item }) => item);
  }

  downloadReport(): void {
    this.competitionImportService.downloadReport(this.initialFilters)
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }
}
